import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { getCandidateFallback, getManagedCandidates } from '@/lib/data/candidates'
import type { Candidate } from '@/types/candidate'

type CandidateDataStatus = 'loading' | 'ready' | 'fallback'

interface CandidateDataContextValue {
  candidates: Candidate[]
  status: CandidateDataStatus
}

const CandidateDataContext = createContext<CandidateDataContextValue | null>(null)

export function CandidateDataProvider({ children }: { children: ReactNode }) {
  const [candidates, setCandidates] = useState<Candidate[]>(getCandidateFallback)
  const [status, setStatus] = useState<CandidateDataStatus>('loading')

  useEffect(() => {
    let ignore = false

    getManagedCandidates()
      .then((items) => {
        if (ignore) return
        setCandidates(items)
        setStatus(items === getCandidateFallback() ? 'fallback' : 'ready')
      })
      .catch(() => {
        if (ignore) return
        setCandidates(getCandidateFallback())
        setStatus('fallback')
      })

    return () => {
      ignore = true
    }
  }, [])

  const value = useMemo(() => ({ candidates, status }), [candidates, status])

  return <CandidateDataContext.Provider value={value}>{children}</CandidateDataContext.Provider>
}

export function useCandidateData() {
  const context = useContext(CandidateDataContext)
  if (!context) throw new Error('useCandidateData debe usarse dentro de CandidateDataProvider')
  return context
}
